import React from 'react';

import { Settings as SettingsIcon } from 'lucide-react';

// Generate initials from name
const getInitials = (name: string): string => {
  if (!name) return 'U';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
};

// Format role for display
const formatRole = (role: string): string => {
  if (!role) return 'Member';
  return role
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const SidebarProfileV2: React.FC<{
  name: string;
  role: string;
  email?: string;
  tenantName?: string; // NEW: Display firm/tenant name
  isAdmin?: boolean;
  isCollapsed?: boolean; // NEW: Collapsed state
  onSettings: () => void;
}> = ({ name, role, email, tenantName, isAdmin, isCollapsed = false, onSettings }) => {
  const initials = getInitials(name);
  const displayRole = formatRole(role);

  // Collapsed view - just show avatar with settings button
  if (isCollapsed) {
    return (
      <div className="relative rounded-2xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm transition-all duration-300">
        <div className="relative p-3 flex flex-col items-center gap-2">
          <div className="relative">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 p-[2px]">
              <div className="grid h-full w-full place-items-center rounded-full bg-white dark:bg-neutral-800 text-sm font-semibold text-amber-600 dark:text-amber-400">
                {initials}
              </div>
            </div>
            {/* Online status indicator */}
            <div className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white dark:border-neutral-800 bg-green-500"></div>
          </div>
          <button
            onClick={onSettings}
            aria-label="Open Settings"
            className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-slate-700 dark:text-neutral-300 shadow-sm hover:bg-slate-50 dark:hover:bg-neutral-700 transition-colors"
            title="Settings"
          >
            <SettingsIcon className="h-3.5 w-3.5 text-brand-600" />
          </button>
          {isAdmin && (
            <div className="w-full flex justify-center">
              <span className="inline-block text-amber-500 text-xs" title="Admin">
                ⭐
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Full view
  return (
    <div className="relative rounded-2xl border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm">
      <div className="relative p-4 pb-3">
        <button
          onClick={onSettings}
          aria-label="Open Settings"
          className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 text-slate-700 dark:text-neutral-300 shadow-sm hover:bg-slate-50 dark:hover:bg-neutral-700 transition-colors"
          title="Settings"
        >
          <SettingsIcon className="h-4 w-4 text-brand-600" />
        </button>
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-600 p-[2px]">
              <div className="grid h-full w-full place-items-center rounded-full bg-white dark:bg-neutral-800 text-sm font-semibold text-amber-600 dark:text-amber-400">
                {initials}
              </div>
            </div>
            {/* Online status indicator */}
            <div className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-white dark:border-neutral-800 bg-green-500"></div>
          </div>
          <div className="min-w-0 flex-1">
            <div
              className="truncate text-sm font-semibold text-slate-900 dark:text-neutral-100"
              title={name}
            >
              {name}
            </div>
            {email && (
              <div className="truncate text-xs text-slate-600 dark:text-neutral-400" title={email}>
                {email}
              </div>
            )}
            <div
              className="truncate text-[10px] text-slate-500 dark:text-neutral-500"
              title={displayRole}
            >
              {displayRole}
            </div>
          </div>
        </div>
        {/* Tenant/Firm Name Display */}
        {tenantName && (
          <div className="mt-3 rounded-lg border border-blue-100 dark:border-blue-900/50 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 px-3 py-2">
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500 dark:bg-blue-600 text-white">
                <span className="text-[10px] font-bold">🏢</span>
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[9px] font-medium uppercase tracking-wide text-blue-600 dark:text-blue-400">
                  Firm
                </div>
                <div
                  className="truncate text-xs font-semibold text-blue-900 dark:text-blue-200"
                  title={tenantName}
                >
                  {tenantName}
                </div>
              </div>
            </div>
          </div>
        )}
        {isAdmin && (
          <div className="mt-3">
            <span className="inline-block rounded-full border border-amber-200 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/30 px-2.5 py-1 text-[10px] font-medium text-amber-700 dark:text-amber-300">
              ⭐ Admin
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SidebarProfileV2;
